
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "@/components/ui/dialog";
import { FaAndroid, FaGlobe } from "react-icons/fa";
import { X } from "lucide-react";

interface DownloadOption {
  icon: React.ElementType;
  title: string;
  description: string;
  link: string;
  isOfficial?: boolean;
}

interface DownloadPopupProps {
  buttonText?: string;
}

const downloadOptions: DownloadOption[] = [{
  icon: FaAndroid,
  title: "Android Version",
  description: "Available on Google Play",
  link: "https://play.google.com/apps/testing/com.RTz.TWCGame",
  isOfficial: true
}, {
  icon: FaGlobe,
  title: "Web Version",
  description: "Play directly in your browser",
  link: "https://web.frontrunnergame.com"
}];

const DownloadPopup = ({ buttonText = "Play Now" }: DownloadPopupProps) => {
  const handleDownloadClick = (platform: string, isOfficial?: boolean) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', isOfficial ? 'official_download_click' : 'download_click', {
        event_category: 'conversion',
        event_label: platform,
        platform: platform,
        download_type: isOfficial ? 'official' : 'alternate',
        location: 'popup'
      });
    }
  };

  const handleDialogOpen = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'download_popup_open', {
        event_category: 'engagement',
        event_label: 'Download Popup Opened'
      });
    }
  };

  return <Dialog onOpenChange={(open) => open && handleDialogOpen()}>
      <DialogTrigger asChild>
        <button className="w-full h-12 group relative px-8 bg-[#f94933] text-white rounded-md font-bold hover:bg-[#f94933]/90 transition-all transform hover:scale-105 hover:shadow-lg hover:shadow-[#f94933]/20 uppercase tracking-wider overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#ff6b6b] to-[#f94933] transform origin-left scale-x-0 transition-transform duration-500 ease-out group-hover:scale-x-100" />
          <span className="relative z-10">
            {buttonText}
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1 ml-2">→</span>
          </span>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-0 sm:max-w-[425px] [&>button]:absolute [&>button]:right-3 [&>button]:top-3 [&>button]:p-0 [&>button]:bg-transparent [&>button]:border-0">
        <DialogTitle className="sr-only">Download Options</DialogTitle>
        <button className="group absolute right-3 top-3 p-2 rounded-full hover:bg-neutral-800 active:bg-neutral-700">
          <X className="h-4 w-4 text-neutral-400" />
        </button>
        <div className="grid gap-6 py-4">
          <div className="text-center px-4">
            <h2 className="text-2xl font-bold text-white mb-2">Choose Your Platform</h2>
            <p className="text-white">Start your racing journey today!</p>
          </div>
          
          <div className="grid gap-4">
            {downloadOptions.map((option, index) => {
              const Icon = option.icon;
              const isAndroid = option.title === "Android Version";
              return (
                <a 
                  key={option.title} 
                  href={option.link} 
                  onClick={() => handleDownloadClick(option.title, option.isOfficial)}
                  className={`group relative flex items-center p-4 rounded-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg ${
                    isAndroid 
                      ? 'bg-gradient-to-r from-[#ff6b6b]/10 to-[#f94933]/10 hover:from-[#ff6b6b]/20 hover:to-[#f94933]/20' 
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                  style={{
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isAndroid 
                      ? 'bg-gradient-to-r from-[#ff6b6b]/20 to-[#f94933]/20 group-hover:from-[#ff6b6b]/30 group-hover:to-[#f94933]/30' 
                      : 'bg-[#f94933]/10 group-hover:bg-[#f94933]/20'
                  }`}>
                    <Icon className="h-6 w-6 text-[#f94933]" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-white group-hover:text-[#f94933] transition-colors">
                      {option.title}
                    </h3>
                    <p className="text-sm text-white">{option.description}</p>
                  </div>
                  <span className="ml-auto text-[#f94933] opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-1">
                    →
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};

export default DownloadPopup;
